// src/pages/ResumoEditorPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { resumoService } from '../services/resumo.service';
import { ResumoData } from '../components/resumo/types';
import EditorToolbar from '../components/resumo/EditorToolbar';
import PageNavigator, { ResumoPage } from '../components/resumo/components/PageNavigator';
import Toast from '../components/Toast';
import { ChevronLeft, ChevronRight, Sun, Moon, Eye, Trash2, Maximize, Minimize, Save, Loader2, ShieldOff, ShieldCheck, Download, Upload, MoreVertical, LayoutList, Link, Copy, Check, Clock } from 'lucide-react';
import apostilaPreviewService, { PreviewDuration, PREVIEW_DURATION_LABELS } from '../services/apostilaPreview.service';
import './ResumoEditorPage.css';
import '../components/resumo/FlashcardBadge.css';
import { InlineContentManager } from '../components/resumo/InlineContentManager';
import { PopoverManager } from '../components/resumo/PopoverManager';
import { MediaResizeManager } from '../components/resumo/MediaResizeManager';
// import SimpleRuler from '../components/resumo/ruler/SimpleRuler';
// import { useRulerSync } from '../components/resumo/ruler/useRulerSync';
import { QuizBadgeManager } from '../components/resumo/quiz/QuizBadgeManager';
import QuizAdminModal from '../components/resumo/quiz/QuizAdminModal';
import { QuizBadgeData } from '../components/resumo/quiz/quiz.types';
import '../components/resumo/quiz/QuizCard.css';
import AutoHideFooter from '../components/resumo/layout/AutoHideFooter';
import { TableManager } from '../components/resumo/TableManager';
import '../components/resumo/TableManager.css';
import ImportApostilaModal from '../components/resumo/ImportApostilaModal';
import PageManagerModal from '../components/resumo/PageManagerModal';
import { ensureBlockIds } from '../components/resumo/annotations/blockAnchor';
import ApostilaDraftsModal from '../components/resumo/ApostilaDraftsModal';
import ReviewerPanel, { PageNote } from '../components/resumo/ReviewerPanel';
import { ActiveDraftContext } from '../components/resumo/ReviewerContextBar';
import ReviewerFAB from '../components/resumo/ReviewerFAB';

interface ResumoEditorPageProps {
    isCustomDeck?: boolean;
}

const ResumoEditorPage: React.FC<ResumoEditorPageProps> = ({ isCustomDeck = false }) => {
    const { deckId, subdeckId } = useParams<{ deckId: string; subdeckId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, isApostilaRevisor } = useAuth();

    // ✅ MUDANÇA CRÍTICA: Usar ThemeContext global
    const { theme, toggleTheme } = useTheme();
    const darkMode = theme === 'dark';

    // Estados principais
    const [resumo, setResumo] = useState<ResumoData | null>(null);
    const [pages, setPages] = useState<ResumoPage[]>([]);
    const pagesRef = useRef<ResumoPage[]>([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [cards, setCards] = useState<any[]>([]);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const contentManagerRef = useRef<InlineContentManager | null>(null);
    const popoverManagerRef = useRef<PopoverManager | null>(null);
    const resizeManagerRef = useRef<MediaResizeManager | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Refs
    const editorRef = useRef<HTMLDivElement>(null);
    const savedRangeRef = useRef<Range | null>(null);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const lastSavedHTMLRef = useRef<string>('');
    // const { rulerState, updateSelection, applyIndent } = useRulerSync(editorRef, isDataLoaded);
    const tableManagerRef = useRef<TableManager | null>(null);

    // ✅ QUIZ: Estados
    const [quizManager, setQuizManager] = useState<QuizBadgeManager | null>(null);
    const [quizModalOpen, setQuizModalOpen] = useState(false);
    const [editingQuizId, setEditingQuizId] = useState<string | null>(null);
    const [editingQuizData, setEditingQuizData] = useState<QuizBadgeData | null>(null);

    const [isBlocked, setIsBlocked] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showPageManager, setShowPageManager] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Modo revisor
    const revisorMode = (location.state as any)?.mode === 'revisor';
    // Editor Ryan (role 'editor') vindo do RyanFieldPage/ResumoReadPage — edita
    // direto, com os mesmos poderes de conteúdo do admin, sem rascunho/aprovação
    const isRyanEditor = (location.state as any)?.isRyanEditor === true;
    const [showDraftsModal, setShowDraftsModal] = useState(false);
    const [pendingDraftsCount, setPendingDraftsCount] = useState(0);

    // Liberar Prévia
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewDuration, setPreviewDuration] = useState<PreviewDuration>('60min');
    const [previewPagesLimit, setPreviewPagesLimit] = useState<number>(0); // 0 = todas
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState<string | null>(null);
    const [previewExpiresAt, setPreviewExpiresAt] = useState<string | null>(null);
    const [previewCopied, setPreviewCopied] = useState(false);
    const [activePreviewTokens, setActivePreviewTokens] = useState<import('../services/apostilaPreview.service').ActivePreviewToken[]>([]);
    const [isLoadingActiveTokens, setIsLoadingActiveTokens] = useState(false);
    // Incrementado sempre que pages são trocadas externamente (draft load),
    // forçando o effect de conteúdo do editor a re-executar.
    const [editorLoadTrigger, setEditorLoadTrigger] = useState(0);
    // Painel e dados de anotação do revisor
    const [showReviewerPanel, setShowReviewerPanel] = useState(false);
    const [reviewNote, setReviewNote] = useState('');
    const [pageNotes, setPageNotes] = useState<PageNote[]>([]);
    // Contexto do rascunho carregado pelo admin (barra de contexto)
    const [activeDraft, setActiveDraft] = useState<ActiveDraftContext | null>(null);

    // Manter pagesRef sempre sincronizado com pages
    useEffect(() => {
        pagesRef.current = pages;
    }, [pages]);

    useEffect(() => {
        if (!isDataLoaded || !editorRef.current) {
            console.log('⏳ Aguardando editor e dados...', {
                isDataLoaded,
                hasEditor: !!editorRef.current
            });
            return;
        }

        console.log('🔧 Inicializando managers...', {
            hasEditor: !!editorRef.current
        });

        // ✅ Criar managers
        contentManagerRef.current = new InlineContentManager(editorRef, cards);
        popoverManagerRef.current = new PopoverManager(editorRef, cards, darkMode);
        resizeManagerRef.current = new MediaResizeManager(editorRef);

        console.log('✅ Managers criados:', {
            hasContentManager: !!contentManagerRef.current,
            hasPopoverManager: !!popoverManagerRef.current,
            hasResizeManager: !!resizeManagerRef.current
        });

        // ✅ QUIZ: Inicializar manager
        const quizMgr = new QuizBadgeManager(
            editorRef,
            (quizId, data) => {
                setEditingQuizId(quizId);
                setEditingQuizData(data);
                setQuizModalOpen(true);
            },
            undefined,
            () => {
                console.log('🔥 Quiz alterado → marcando resumo como sujo');
                setHasUnsavedChanges(true);
            }
        );
        setQuizManager(quizMgr);

        console.log('✅ QuizManager criado:', {
            hasQuizManager: !!quizMgr
        });

        // ✅ Inicializar
        contentManagerRef.current.initializeAllBadges(); // já chama initializeAllCallouts internamente
        resizeManagerRef.current.initializeAllMedia();

        tableManagerRef.current = new TableManager(
            editorRef,
            () => {
                console.log('🔥 Tabela alterada → marcando resumo como sujo');
                setHasUnsavedChanges(true);
            }
        );

        // ✅ Cleanup
        return () => {
            console.log('🧹 Cleanup dos managers');
            contentManagerRef.current?.cleanup();
            popoverManagerRef.current?.cleanup();
            resizeManagerRef.current?.cleanup();
            tableManagerRef.current?.cleanup();
            quizMgr?.cleanup();
        };
    }, [isDataLoaded, cards, darkMode]);

    // Adicionar no useEffect de inicialização do editor, ou como handler de paste
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const handlePaste = (e: ClipboardEvent) => {
            e.preventDefault();
            const html = e.clipboardData?.getData('text/html') || '';
            const plain = e.clipboardData?.getData('text/plain') || '';

            if (html) {
                const temp = document.createElement('div');
                temp.innerHTML = html;
                const tableElements = new Set(['TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH']);

                temp.querySelectorAll<HTMLElement>('*').forEach(el => {
                    if (!tableElements.has(el.tagName)) {
                        el.style.removeProperty('color');
                        el.style.removeProperty('background-color');
                        el.style.removeProperty('background');
                        el.style.removeProperty('font-family');
                    }
                });

                document.execCommand('insertHTML', false, temp.innerHTML);
            } else if (plain) {
                document.execCommand('insertText', false, plain);
            }
        };

        editor.addEventListener('paste', handlePaste);
        return () => editor.removeEventListener('paste', handlePaste);
    }, [isDataLoaded]);

    // ✅ NOVO: Importar quizzes quando manager estiver pronto
    useEffect(() => {
        if (!quizManager || !resumo?.quizzes) return;

        console.log('📥 Importando quizzes para o manager:', {
            count: Object.keys(resumo.quizzes).length,
            ids: Object.keys(resumo.quizzes)
        });

        quizManager.importQuizzes(resumo.quizzes);

        setTimeout(() => {
            quizManager.initializeAllBadges(true);
            console.log('✅ Quizzes importados e badges inicializados');
        }, 150);

    }, [quizManager, resumo?.quizzes]);

    // ✅ PATCH 1: Re-inicializar quando dados mudarem
    useEffect(() => {
        const timer = setTimeout(() => {
            if (contentManagerRef.current && isDataLoaded) {
                console.log('🔄 Re-inicializando conteúdo inline...');
                contentManagerRef.current.initializeAllBadges();
            }

            if (resizeManagerRef.current && isDataLoaded) {
                resizeManagerRef.current.initializeAllMedia();
            }

            if (tableManagerRef.current && isDataLoaded) {
                tableManagerRef.current.initializeAllTables();
            }

            // ✅ QUIZ: Re-inicializar badges
            if (quizManager && isDataLoaded) {
                setTimeout(() => {
                    quizManager.initializeAllBadges(true);
                    console.log('✅ Quiz badges re-inicializados');
                }, 100);
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [isDataLoaded, currentPageIndex, quizManager]);

    // ✅ PATCH 2: Listener de visibilidade (volta do preview)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && contentManagerRef.current) {
                console.log('👁️ Página ficou visível, re-inicializando...');

                setTimeout(() => {
                    contentManagerRef.current?.initializeAllBadges();
                    resizeManagerRef.current?.initializeAllMedia();
                    tableManagerRef.current?.initializeAllTables();
                    console.log('✅ Re-inicialização completa');
                }, 200);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // ✅ NOVO: Atualizar PopoverManager quando dados mudarem
    useEffect(() => {
        if (popoverManagerRef.current) {
            popoverManagerRef.current.updateData(cards, darkMode);
        }
    }, [cards, darkMode]);

    // Auto-open import modal quando navegado com state openImport
    useEffect(() => {
        if ((location.state as any)?.openImport) {
            setShowImportModal(true);
            window.history.replaceState({}, '');
        }
    }, [location.state]);

    // Verificar permissão
    useEffect(() => {
        if (!isAdmin && !isApostilaRevisor && !isRyanEditor) {
            navigate('/dashboard');
            return;
        }

        console.log('🔄 Componente montado, carregando resumo...');
        setIsDataLoaded(false);
        loadResumo();

        // ✅ Capturar AQUI, fora do cleanup
        const currentTimeout = saveTimeoutRef.current;

        return () => {
            console.log('🔄 Componente desmontado');
            if (currentTimeout) {
                clearTimeout(currentTimeout);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deckId, subdeckId, isAdmin, isApostilaRevisor, isRyanEditor]);

    // Carregar rascunho do revisor após os dados da apostila oficial estarem prontos
    useEffect(() => {
        if (!revisorMode || !isDataLoaded || !deckId || !subdeckId) return;
        const token = localStorage.getItem('accessToken');

        fetch(`/api/apostila-draft/${deckId}/${subdeckId}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(data => {
                if (data.success && data.draft && data.draft.pages?.length > 0) {
                    setPages(data.draft.pages);
                    if (data.draft.quizzes) {
                        setResumo(prev => prev ? { ...prev, quizzes: data.draft.quizzes } : prev);
                    }
                    if (data.draft.reviewNote) setReviewNote(data.draft.reviewNote);
                    if (data.draft.pageNotes)  setPageNotes(data.draft.pageNotes);
                    setCurrentPageIndex(0);
                    setEditorLoadTrigger(t => t + 1);
                    console.log('✅ Rascunho do revisor carregado');
                }
            })
            .catch(() => { /* sem rascunho ainda, usa conteúdo oficial */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisorMode, isDataLoaded, deckId, subdeckId]);

    // Buscar contagem de rascunhos pendentes para o admin
    useEffect(() => {
        if (!isAdmin || !deckId || !subdeckId) return;
        const token = localStorage.getItem('accessToken');

        fetch(`/api/apostila-draft/${deckId}/${subdeckId}/admin`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    const pending = (data.drafts as any[]).filter(d => d.status === 'pending').length;
                    setPendingDraftsCount(pending);
                }
            })
            .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, deckId, subdeckId]);

    useEffect(() => {
        if (!editorRef.current || !pagesRef.current[currentPageIndex]) return;

        const currentPage = pagesRef.current[currentPageIndex];
        const currentContent = currentPage.content || '';

        editorRef.current.innerHTML = currentContent;
        lastSavedHTMLRef.current = currentContent;

    // editorLoadTrigger: força re-execução quando pages são trocadas externamente (draft load)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageIndex, isDataLoaded, editorLoadTrigger]);

    // HTML do editor sem contenteditable nos callout-bodies (atributo é apenas runtime, não deve ser persistido)
    const getCleanEditorHTML = () => {
        if (!editorRef.current) return '';
        const clone = editorRef.current.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('.callout-body[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
        return ensureBlockIds(clone.innerHTML);
    };

    const saveAsDraft = useCallback(async () => {
        if (!deckId || !subdeckId || !isDataLoaded) return;

        const currentHTML = getCleanEditorHTML();
        if (!currentHTML || currentHTML.trim().length === 0) {
            setToast({ message: 'Editor vazio, nada a salvar', type: 'info' });
            return;
        }

        setIsSaving(true);
        try {
            const quizzes = quizManager?.exportAllQuizzes() || {};
            const updatedPages = pages.map((page, index) =>
                index === currentPageIndex ? { ...page, content: currentHTML } : page
            );

            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/apostila-draft/${deckId}/${subdeckId}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ pages: updatedPages, quizzes, reviewNote, pageNotes })
            });

            const data = await res.json();
            if (data.success) {
                setPages(updatedPages);
                setHasUnsavedChanges(false);
                lastSavedHTMLRef.current = currentHTML;
                setToast({ message: 'Rascunho enviado ao administrador', type: 'success' });
            } else {
                setToast({ message: 'Erro ao salvar rascunho', type: 'error' });
            }
        } catch {
            setToast({ message: 'Erro ao salvar rascunho', type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }, [deckId, subdeckId, isDataLoaded, pages, currentPageIndex, quizManager, reviewNote, pageNotes]);

    const autoSave = useCallback(async () => {
        // Validações iniciais
        if (!deckId || !subdeckId || !resumo) {
            console.warn('⚠️ Autosave cancelado: faltam dados básicos');
            return;
        }

        if (!isDataLoaded) {
            console.warn('⚠️ Autosave cancelado: dados ainda não carregados');
            return;
        }

        // 1️⃣ CAPTURA HTML DIRETO DO EDITOR (FONTE DA VERDADE)
        const currentHTML = getCleanEditorHTML();

        // 2️⃣ VALIDAÇÕES COM HTML ATUAL
        console.log(`📊 Tamanho do HTML atual: ${currentHTML.length} caracteres (${(currentHTML.length / 1024).toFixed(2)} KB)`);

        if (!currentHTML || currentHTML.trim().length === 0) {
            console.warn('⚠️ Autosave cancelado: editor vazio');
            return;
        }

        // Limite pelo TAMANHO TOTAL da apostila, não por página: antes, uma página
        // acima de ~490 KB cancelava o salvamento com um toast "Reduzindo..." que
        // não reduzia nada — apostilas grandes (3-4 MB) nunca salvavam. A apostila
        // inteira é 1 documento no MongoDB (teto 16 MB); o salvamento vai direto ao
        // backend (express aceita 50 MB). 12 MB deixa folga pro resto do documento.
        // Soma de caracteres ≈ bytes (HTML é quase todo ASCII); a página atual entra
        // 2x porque também vai em `blocks`.
        const MAX_APOSTILA_BYTES = 12 * 1024 * 1024;
        const estimatedBytes = pages.reduce(
            (sum, page, index) => sum + (index === currentPageIndex ? currentHTML.length : (page.content || '').length),
            0
        ) + currentHTML.length;
        if (estimatedBytes > MAX_APOSTILA_BYTES) {
            setToast({
                message: `Apostila grande demais para salvar (${(estimatedBytes / 1024 / 1024).toFixed(1)} MB; limite 12 MB). ` +
                    'Normalmente são imagens coladas direto no texto — envie-as pelo botão de imagem, que as guarda fora da apostila.',
                type: 'error'
            });
            return;
        }

        setIsSaving(true);
        console.log('💾 Iniciando autosave...');

        try {
            // 3️⃣ Exporta quizzes
            const quizzes = quizManager?.exportAllQuizzes() || {};
            console.log('🔍 [AUTOSAVE] Exportando quizzes:', {
                hasQuizManager: !!quizManager,
                quizzesCount: Object.keys(quizzes).length
            });

            // 4️⃣ Monta pages LOCALMENTE
            const updatedPages = pages.map((page, index) =>
                index === currentPageIndex
                    ? { ...page, content: currentHTML }
                    : page
            );

            // 5️⃣ Monta payload
            const resumoToSave = {
                ...resumo!,
                blocks: [
                    {
                        id: 'main',
                        type: 'paragraph' as const,
                        content: currentHTML
                    }
                ],
                pages: updatedPages,
                quizzes,
                metadata: {
                    ...resumo!.metadata,
                    lastEdited: new Date().toISOString(),
                    version: resumo!.metadata.version || 1
                }
            };

            console.log('📤 [AUTOSAVE] Payload final:', {
                htmlLength: currentHTML.length,
                pagesCount: updatedPages.length,
                quizzesCount: Object.keys(quizzes).length
            });

            // 6️⃣ Envia para backend
            await resumoService.saveResumo(deckId, subdeckId, resumoToSave, isCustomDeck);

            // 7️⃣ Atualiza state
            setPages(updatedPages);
            setLastSaved(new Date());
            setHasUnsavedChanges(false);
            lastSavedHTMLRef.current = currentHTML;
            setActiveDraft(null);

            console.log('✅ Autosave concluído com sucesso');

        } catch (error) {
            console.error('❌ Erro no autosave:', error);
            setToast({ message: 'Erro ao salvar. Suas alterações podem não ter sido salvas.', type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }, [deckId, subdeckId, resumo, isDataLoaded, pages, currentPageIndex, quizManager, isCustomDeck]);

    // ✅ Ajustar classes da toolbar E régua quando header some/aparece
    useEffect(() => {
        const header = document.querySelector('.rep-header') as HTMLElement;
        const toolbar = document.querySelector('.et-toolbar') as HTMLElement;
        const ruler = document.querySelector('.simple-ruler') as HTMLElement;

        if (!header || !toolbar || !ruler) {
            return;
        }

        const observer = new MutationObserver(() => {
            const isHeaderHidden = header.classList.contains('hidden');

            if (isHeaderHidden) {
                // Header escondido - adiciona classes
                toolbar.classList.add('header-hidden');
                toolbar.classList.remove('header-visible');
                ruler.classList.add('header-hidden');
                ruler.classList.remove('header-visible');
            } else {
                // Header visível - remove classes
                toolbar.classList.remove('header-hidden');
                toolbar.classList.add('header-visible');
                ruler.classList.remove('header-hidden');
                ruler.classList.add('header-visible');
            }
        });

        observer.observe(header, {
            attributes: true,
            attributeFilter: ['class']
        });

        // Listener de scroll
        let lastScroll = 0;

        const handleScroll = () => {
            // ✅ No mobile, header e toolbar sempre fixos — não esconder
            if (window.innerWidth <= 768) return;

            const currentScroll = window.scrollY;
            if (currentScroll > lastScroll && currentScroll > 100) {
                header.classList.add('hidden');
            } else if (currentScroll < lastScroll) {
                header.classList.remove('hidden');
            }
            lastScroll = currentScroll;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Desselecionar mídia ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.media-container')) {
                document.querySelectorAll('.media-container.selected').forEach(el => {
                    el.classList.remove('selected');
                });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fechar menu ao clicar fora
    useEffect(() => {
        const handleMenuClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleMenuClickOutside);
        return () => document.removeEventListener('mousedown', handleMenuClickOutside);
    }, []);

    // Listener de fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Carregar resumo
    const loadResumo = async () => {
        if (!deckId || !subdeckId) return;

        try {
            setLoading(true);

            let resumoData: any = null;

            try {
                const data = await resumoService.getResumo(deckId, subdeckId, isCustomDeck);
                resumoData = data.resumo;

                console.log('📥 Dados recebidos do backend:', {
                    title: data.resumo.title,
                    pagesCount: data.resumo.pages?.length || 0,
                    blocksCount: data.resumo.blocks?.length || 0,
                    quizzesCount: data.resumo.quizzes ? Object.keys(data.resumo.quizzes).length : 0
                });

                if (data.resumo.pages && data.resumo.pages.length > 0) {
                    setPages(data.resumo.pages);
                } else {
                    const defaultPage: ResumoPage = {
                        id: `page_${Date.now()}`,
                        title: '',
                        content: '',
                        order: 0
                    };
                    setPages([defaultPage]);
                }

                setResumo(data.resumo);
                setIsBlocked(data.resumo.isBlocked || false);

            } catch (error: any) {
                if (error.message.includes('não encontrado')) {
                    const newResumo: ResumoData = {
                        title: 'Novo Resumo',
                        blocks: [],
                        metadata: {
                            subdeckId: subdeckId,
                            subdeckName: '',
                            deckId: deckId,
                            deckName: '',
                            lastEdited: new Date().toISOString(),
                            version: 1
                        },
                        isPublished: true
                    };
                    setResumo(newResumo);
                    resumoData = newResumo;

                    const defaultPage: ResumoPage = {
                        id: `page_${Date.now()}`,
                        title: '',
                        content: '',
                        order: 0
                    };
                    setPages([defaultPage]);
                } else {
                    throw error;
                }
            }

            // ✅ SALVAR quizzes em um state temporário para importar depois
            if (resumoData && resumoData.quizzes) {
                console.log('🔄 Quizzes recebidos do backend:', {
                    count: Object.keys(resumoData.quizzes).length,
                    ids: Object.keys(resumoData.quizzes)
                });

                // Guardar em state para ser importado quando o manager estiver pronto
                setResumo({
                    ...resumoData,
                    quizzes: resumoData.quizzes
                });
            } else {
                console.log('⚠️ Nenhum quiz encontrado no backend');
                setResumo(resumoData);
            }

            const cardsData = await resumoService.getSubdeckCards(deckId, subdeckId, isCustomDeck);
            setCards(cardsData);

        } catch (error: any) {
            console.error('Erro ao carregar resumo:', error);
            setToast({ message: 'Erro ao carregar resumo', type: 'error' });
        } finally {
            setLoading(false);
            setIsDataLoaded(true);
            console.log('✅ Dados carregados, editor pronto para uso');
        }
    };

    // ── 4. HANDLER: Toggle blocked ─────────────────────────────────────────────

    const handleToggleBlocked = async () => {
        if (!deckId || !subdeckId) return;

        const action = isBlocked ? 'liberar' : 'interditar';
        if (!window.confirm(`Tem certeza que deseja ${action} esta apostila?`)) return;

        try {
            const result = await resumoService.toggleBlocked(deckId, subdeckId, isCustomDeck);
            setIsBlocked(result.isBlocked);
            setToast({
                message: result.message,
                type: 'info'
            });
        } catch (error: any) {
            setToast({ message: 'Erro ao alterar status', type: 'error' });
        }
    };

    // ── HANDLER: Gerar token de prévia ────────────────────────────────────────

    const loadActivePreviewTokens = async () => {
        if (!deckId || !subdeckId) return;
        setIsLoadingActiveTokens(true);
        try {
            const authToken = localStorage.getItem('accessToken') || '';
            const tokens = await apostilaPreviewService.listActiveTokens(deckId, subdeckId, authToken);
            setActivePreviewTokens(tokens);
        } catch {
            // silencioso
        } finally {
            setIsLoadingActiveTokens(false);
        }
    };

    const handleGeneratePreview = async () => {
        if (!deckId || !subdeckId) return;
        setIsGeneratingPreview(true);
        try {
            const authToken = localStorage.getItem('accessToken') || '';
            const limit = previewPagesLimit > 0 ? previewPagesLimit : null;
            const result = await apostilaPreviewService.generateToken(deckId, subdeckId, previewDuration, limit, authToken);
            const url = `${window.location.origin}/previa/${result.urlSlug}`;
            setGeneratedPreviewUrl(url);
            setPreviewExpiresAt(result.expiresAt);
            // Recarregar lista de ativos
            loadActivePreviewTokens();
        } catch (err: any) {
            setToast({ message: err?.response?.data?.message || 'Erro ao gerar prévia', type: 'error' });
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    const handleCopyPreviewUrl = () => {
        if (!generatedPreviewUrl) return;
        navigator.clipboard.writeText(generatedPreviewUrl).then(() => {
            setPreviewCopied(true);
            setTimeout(() => setPreviewCopied(false), 2500);
        });
    };

    const handleRevokePreviewToken = async (urlSlug: string) => {
        try {
            const authToken = localStorage.getItem('accessToken') || '';
            await apostilaPreviewService.revokeToken(urlSlug, authToken);
            setActivePreviewTokens(prev => prev.filter(t => t.urlSlug !== urlSlug));
        } catch {
            setToast({ message: 'Erro ao revogar prévia', type: 'error' });
        }
    };

    const handleOpenPreviewModal = () => {
        setShowPreviewModal(true);
        setShowMenu(false);
        loadActivePreviewTokens();
    };

    const handleClosePreviewModal = () => {
        setShowPreviewModal(false);
        setGeneratedPreviewUrl(null);
        setPreviewExpiresAt(null);
        setPreviewCopied(false);
        setPreviewDuration('60min');
        setPreviewPagesLimit(0);
    };

    // ── 5. HANDLER: Export apostila ────────────────────────────────────────────

    const handleExport = () => {
        if (!resumo) return;

        // Salva página atual antes de exportar
        saveCurrentPage();

        // Pequeno delay para garantir que pagesRef foi atualizado
        setTimeout(() => {
            resumoService.exportApostila(
                resumo,
                pagesRef.current,
                resumo.metadata.deckName || 'deck',
                resumo.metadata.subdeckName || 'subdeck'
            );
            setToast({ message: 'Apostila exportada com sucesso!', type: 'success' });
        }, 100);
    };

    // ── 6. HANDLER: Import apostila ────────────────────────────────────────────

    const handleImport = async (importedDataList: any[]) => {
        const importedData = importedDataList[0];
        if (!deckId || !subdeckId || !resumo || !importedData) return;

        try {
            // Monta o payload com os dados importados
            const resumoToSave = {
                ...resumo,
                title: importedData.title || resumo.title,
                blocks: importedData.blocks || [],
                pages: importedData.pages || [],
                quizzes: importedData.quizzes || {},
                metadata: {
                    ...resumo.metadata,
                    lastEdited: new Date().toISOString()
                }
            };

            await resumoService.saveResumo(deckId, subdeckId, resumoToSave, isCustomDeck);

            // Atualiza estado local
            setPages(importedData.pages || []);
            setResumo(resumoToSave);
            setCurrentPageIndex(0);
            setShowImportModal(false);

            // Recarrega para garantir que quizzes e badges são inicializados
            setTimeout(() => {
                loadResumo();
            }, 300);

            setToast({ message: 'Apostila importada com sucesso!', type: 'success' });
        } catch (error: any) {
            setToast({ message: 'Erro ao importar apostila', type: 'error' });
        }
    };

    // Retorna o HTML do editor sem atributos contenteditable nos callout-bodies (não devem ser persistidos)
    // Salvar página atual antes de trocar
    const saveCurrentPage = () => {
        if (editorRef.current && pages[currentPageIndex]) {
            const html = getCleanEditorHTML();

            const currentContent = pages[currentPageIndex].content || '';
            if (!html || html.trim() === '') {
                if (currentContent && currentContent.length > 0) {
                    console.warn('⚠️ Ignorando tentativa de salvar página vazia sobre conteúdo existente');
                    return;
                }
            }

            const updatedPages = [...pages];
            updatedPages[currentPageIndex] = {
                ...updatedPages[currentPageIndex],
                content: html
            };
            setPages(updatedPages);

            console.log(`💾 Página ${currentPageIndex + 1} salva localmente (${html.length} caracteres)`);
        }
    };

    
    // ✅ Reset de scroll centralizado — chamado por handlePageChange
    // independente de quem disparou a troca (header, footer, ou qualquer
    // navegador futuro), pra nunca mais depender de cada chamador lembrar
    // de resetar o scroll sozinho (era isso que fazia o header não voltar
    // ao topo enquanto o footer, via PageNavigator, funcionava certo).
    const resetScrollToTop = () => {
        const isFullscreen = !!document.fullscreenElement;
        if (isFullscreen) {
            (document.fullscreenElement as HTMLElement).scrollTo({ top: 0, behavior: 'auto' });
        } else {
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    };

    // Mudar de página com transição suave
    const handlePageChange = (newIndex: number) => {
        if (newIndex === currentPageIndex) return;

        console.log(`📄 Mudando para página ${newIndex + 1}`);

        // ✅ Salva página atual antes de sair
        if (editorRef.current && editorRef.current.innerHTML.trim().length > 0) {
            saveCurrentPage();
        } else {
            console.log('⚠️ Editor vazio, não salvando página atual');
        }

        if (!editorRef.current) {
            setCurrentPageIndex(newIndex);
            resetScrollToTop();
            return;
        }

        // Anima saída
        editorRef.current.style.opacity = '0';
        editorRef.current.style.transform = 'translateY(20px)';

        setTimeout(() => {
            setCurrentPageIndex(newIndex);
            resetScrollToTop();

            // Anima entrada
            if (editorRef.current) {
                editorRef.current.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

                setTimeout(() => {
                    if (editorRef.current) {
                        editorRef.current.style.opacity = '1';
                        editorRef.current.style.transform = 'translateY(0)';
                    }

                    // ✅ PASSO 10 — Re-inicializar badges e mídias após trocar de página
                    setTimeout(() => {
                        if (contentManagerRef.current) {
                            contentManagerRef.current.initializeAllBadges();
                        }
                        if (resizeManagerRef.current) {
                            resizeManagerRef.current.initializeAllMedia();
                        }
                    }, 100);
                }, 50);
            }
        }, 300);
    };

    // ✅ CORREÇÃO CRÍTICA: Adicionar nova página VAZIA
    const handleAddPage = () => {
        // 1️⃣ Salva página atual antes
        if (editorRef.current && editorRef.current.innerHTML.trim().length > 0) {
            saveCurrentPage();
        }

        // 2️⃣ Cria nova página VAZIA com ID único
        const newPage: ResumoPage = {
            id: `page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            title: '',
            content: '',
            order: currentPageIndex + 1
        };

        // 3️⃣ Insere APÓS a página atual (não no final)
        const updatedPages = [
            ...pages.slice(0, currentPageIndex + 1),
            newPage,
            ...pages.slice(currentPageIndex + 1)
        ].map((page, index) => ({ ...page, order: index }));

        setPages(updatedPages);

        // 4️⃣ Navega para a nova página (logo após a atual)
        const newIndex = currentPageIndex + 1;
        setCurrentPageIndex(newIndex);

        // 5️⃣ Limpa editor após um pequeno delay
        setTimeout(() => {
            if (editorRef.current) {
                editorRef.current.innerHTML = '';
                editorRef.current.focus();
            }
        }, 100);

        setToast({ message: 'Nova página criada!', type: 'success' });
        console.log('✅ Nova página criada após página', currentPageIndex + 1);
    };

    const handleDeletePage = (index: number) => {
        if (pages.length <= 1) return;
        saveCurrentPage();

        const updatedPages = pages
            .filter((_, i) => i !== index)
            .map((page, i) => ({ ...page, order: i }));

        setPages(updatedPages);
        setHasUnsavedChanges(true);

        const newIndex = index >= updatedPages.length ? updatedPages.length - 1 : index;
        setCurrentPageIndex(newIndex);

        setToast({ message: 'Página excluída', type: 'info' });
    };

    const handleMovePage = (fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= pages.length) return;
        saveCurrentPage();

        const updatedPages = [...pages];
        const [moved] = updatedPages.splice(fromIndex, 1);
        updatedPages.splice(toIndex, 0, moved);
        const reordered = updatedPages.map((page, i) => ({ ...page, order: i }));

        setPages(reordered);
        setHasUnsavedChanges(true);

        const newCurrent = fromIndex === currentPageIndex
            ? toIndex
            : currentPageIndex === toIndex
                ? fromIndex
                : currentPageIndex;
        setCurrentPageIndex(newCurrent);
    };

    const handleInsertPageAfter = (afterIndex: number) => {
        saveCurrentPage();
        const newPage: ResumoPage = {
            id: `page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            title: '',
            content: '',
            order: afterIndex + 1
        };
        const updatedPages = [
            ...pages.slice(0, afterIndex + 1),
            newPage,
            ...pages.slice(afterIndex + 1)
        ].map((page, i) => ({ ...page, order: i }));
        setPages(updatedPages);
        setCurrentPageIndex(afterIndex + 1);
        setHasUnsavedChanges(true);
        setTimeout(() => {
            if (editorRef.current) {
                editorRef.current.innerHTML = '';
                editorRef.current.focus();
            }
        }, 100);
        setToast({ message: 'Página inserida!', type: 'success' });
    };

    const handleRenamePage = (index: number, newTitle: string) => {
        const updatedPages = pages.map((page, i) =>
            i === index ? { ...page, title: newTitle } : page
        );
        setPages(updatedPages);
        setHasUnsavedChanges(true);
    };

    // Deletar resumo
    const handleDelete = async () => {
        if (!deckId || !subdeckId) return;
        if (!window.confirm('Tem certeza que deseja excluir este resumo permanentemente?')) return;

        try {
            console.log('🗑️ [FRONTEND] Deletando resumo:', { deckId, subdeckId, isCustomDeck });

            await resumoService.deleteResumo(deckId, subdeckId, isCustomDeck);

            console.log('✅ [FRONTEND] Resumo deletado com sucesso');

            // ✅ DISPARAR EVENTO GLOBAL
            const event = new CustomEvent('resumo-deleted', {
                detail: { deckId, subdeckId, isCustomDeck }
            });
            window.dispatchEvent(event);

            console.log('📡 [FRONTEND] Evento resumo-deleted disparado');

            setToast({ message: 'Resumo excluído com sucesso', type: 'success' });

            setTimeout(() => goBack(), 1500);
        } catch (error: any) {
            console.error('❌ [FRONTEND] Erro ao excluir:', error);
            setToast({ message: 'Erro ao excluir resumo', type: 'error' });
        }
    };

    // ✅ QUIZ: Handler de salvar quiz
    // ✅ QUIZ: Handler de salvar quiz
    const handleSaveQuiz = (data: QuizBadgeData) => {
        if (!editingQuizId || !quizManager) return;

        console.log('💾 Salvando quiz:', editingQuizId, data);

        // 1️⃣ Salvar dados no manager
        quizManager.saveQuizData(editingQuizId, data);

        // 2️⃣ Forçar salvamento do HTML atual
        saveCurrentPage();

        // 3️⃣ Marcar como alterado
        setHasUnsavedChanges(true);

        // 4️⃣ FORÇAR autosave imediato
        setTimeout(() => {
            autoSave();
        }, 500);

        // 5️⃣ Fechar modal
        setQuizModalOpen(false);
        setEditingQuizId(null);
        setEditingQuizData(null);

        setToast({
            message: 'Quiz salvo com sucesso!',
            type: 'success'
        });

        console.log('✅ Quiz salvo e autosave agendado:', editingQuizId);
    };

    // ✅ QUIZ: Handler de excluir quiz (remove o cartão da página inteira)
    const handleDeleteQuiz = () => {
        if (!editingQuizId || !quizManager) return;

        quizManager.removeQuiz(editingQuizId);

        saveCurrentPage();
        setHasUnsavedChanges(true);

        setTimeout(() => {
            autoSave();
        }, 500);

        setQuizModalOpen(false);
        setEditingQuizId(null);
        setEditingQuizData(null);

        setToast({
            message: 'Quiz excluído',
            type: 'info'
        });

        console.log('🗑️ Quiz excluído e autosave agendado:', editingQuizId);
    };

    // Toggle fullscreen
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            const elem = document.documentElement;
            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if ((elem as any).webkitRequestFullscreen) {
                (elem as any).webkitRequestFullscreen();
            } else if ((elem as any).msRequestFullscreen) {
                (elem as any).msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            } else if ((document as any).msExitFullscreen) {
                (document as any).msExitFullscreen();
            }
            setIsFullscreen(false);
        }
    };

    // Salvar seleção atual
    const saveCurrentSelection = () => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0);
        }
    };

    // Navegação para preview
    const goToPreview = () => {
        saveCurrentPage();

        const timeout = saveTimeoutRef.current;
        if (timeout) {
            clearTimeout(timeout);
        }

        console.log('🔍 Navegando para preview...');

        const fromRyan = (location.state as any)?.fromRyan === true;
        const ryanReturnPath = (location.state as any)?.returnPath;

        const previewPath = isCustomDeck
            ? `/custom-decks/${deckId}/resumo/${subdeckId}`
            : `/resumo/${deckId}/${subdeckId}`;

        // ✅ Preserva o contexto Ryan para não cair no redirect de hostname (App.tsx)
        navigate(previewPath, {
            state: fromRyan ? { fromRyan: true, returnPath: ryanReturnPath } : undefined
        });
    };

    const goBack = () => {
        const fromRyan = (location.state as any)?.fromRyan === true;
        const ryanReturnPath = (location.state as any)?.returnPath;

        if (fromRyan && ryanReturnPath) {
            // ✅ Volta para o ResumoReadPage (não pula direto pro RyanFieldPage) —
            // é o próprio ResumoReadPage quem sabe voltar pro returnPath do Ryan.
            // isRyanEditor é preservado pra o botão "Editar" continuar aparecendo.
            const readPath = isCustomDeck
                ? `/custom-decks/${deckId}/resumo/${subdeckId}`
                : `/resumo/${deckId}/${subdeckId}`;
            navigate(readPath, { state: { fromRyan: true, returnPath: ryanReturnPath, isRyanEditor } });
            return;
        }

        const backPath = isCustomDeck
            ? `/custom-decks/${deckId}/subdecks`
            : `/subdecks/${deckId}`;
        navigate(backPath);
    };

    if (loading) {
        return (
            <div className={`rep-page ${darkMode ? 'dark' : ''}`}>
                <div className="rep-loading">Carregando editor...</div>
            </div>
        );
    }

    if (!resumo) {
        return (
            <div className={`rep-page ${darkMode ? 'dark' : ''}`}>
                <div className="rep-loading">Erro ao carregar resumo</div>
            </div>
        );
    }

    // ✅ NOVO: Aguarda dados carregarem antes de renderizar editor
    if (!isDataLoaded) {
        return (
            <div className={`rep-page ${darkMode ? 'dark' : ''}`}>
                <div className="rep-loading">Preparando editor...</div>
            </div>
        );
    }

    console.log('🎨 Renderizando EditorToolbar com managers:', {
        hasContentManager: !!contentManagerRef.current
    });

    return (
        <div className={`rep-page ${darkMode ? 'dark' : ''}`}>
            {/* Header */}
            <div className="rep-header">
                {/* Coluna esquerda: só o botão voltar */}
                <div className="rep-header-left">
                    <button className="rep-btn-back" onClick={goBack}>
                        <ChevronLeft size={16} />
                    </button>
                    {revisorMode && (
                        <button
                            className={`rep-revisor-badge ${showReviewerPanel ? 'active' : ''}`}
                            onClick={() => setShowReviewerPanel(v => !v)}
                            title="Abrir painel de anotações"
                        >
                            Modo Revisão
                        </button>
                    )}
                </div>

                {/* Coluna central: navegação de páginas + nome do deck */}
                <div className="rep-header-center">
                    <div className="rep-header-page-nav rep-header-page-nav-compact">
                        <button
                            className="rep-header-nav-btn"
                            onClick={() => handlePageChange(currentPageIndex - 1)}
                            disabled={currentPageIndex === 0}
                            title="Página anterior"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <span className="rep-header-nav-label">
                            {currentPageIndex + 1}/{pages.length}
                        </span>
                        <button
                            className="rep-header-nav-btn"
                            onClick={() => handlePageChange(currentPageIndex + 1)}
                            disabled={currentPageIndex === pages.length - 1}
                            title="Próxima página"
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>

                    <div className="rep-breadcrumb">
                        <div className="rep-breadcrumb-names">
                            <span className="rep-breadcrumb-deck">{resumo.metadata.deckName || 'Deck'}</span>
                            <span className="rep-breadcrumb-subdeck">{resumo.metadata.subdeckName || 'Subdeck'}</span>
                        </div>
                    </div>
                </div>

                <div className="rep-header-actions">
                    <button
                        className="rep-btn-header rep-btn-primary"
                        onClick={() => {
                            saveCurrentPage();
                            if (revisorMode) saveAsDraft();
                            else autoSave();
                        }}
                        disabled={isSaving || !hasUnsavedChanges}
                        title={isSaving ? 'Salvando...' : hasUnsavedChanges ? 'Salvar' : 'Salvo'}
                    >
                        {isSaving ? <Loader2 size={16} className="rep-spin" /> : <Save size={16} />}
                    </button>

                    <button
                        className="rep-btn-header"
                        onClick={goToPreview}
                        title="Visualizar"
                    >
                        <Eye size={16} />
                    </button>

                    {/* Menu de opções */}
                    <div className="rep-menu-wrapper" ref={menuRef}>
                        <button
                            className={`rep-btn-header ${showMenu ? 'rep-btn-menu-active' : ''}`}
                            onClick={() => setShowMenu(!showMenu)}
                            title="Mais opções"
                        >
                            <MoreVertical size={16} />
                        </button>

                        {showMenu && (
                            <div className="rep-menu-dropdown">
                                {lastSaved && (
                                    <div className="rep-menu-info">
                                        Salvo às {lastSaved.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                )}

                                <button className="rep-menu-item" onClick={() => { toggleTheme(); setShowMenu(false); }}>
                                    {darkMode ? <Sun size={15} /> : <Moon size={15} />}
                                    <span>{darkMode ? 'Modo Claro' : 'Modo Escuro'}</span>
                                </button>

                                <button className="rep-menu-item" onClick={() => { toggleFullscreen(); setShowMenu(false); }}>
                                    {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
                                    <span>{isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}</span>
                                </button>

                                <button className="rep-menu-item" onClick={() => { setShowPageManager(true); setShowMenu(false); }}>
                                    <LayoutList size={15} />
                                    <span>Gerenciar páginas</span>
                                </button>

                                {!revisorMode && (
                                    <button className="rep-menu-item" onClick={() => { setShowImportModal(true); setShowMenu(false); }}>
                                        <Upload size={15} />
                                        <span>Importar apostila</span>
                                    </button>
                                )}

                                <button className="rep-menu-item" onClick={() => { handleExport(); setShowMenu(false); }}>
                                    <Download size={15} />
                                    <span>Exportar apostila</span>
                                </button>

                                {isAdmin && !revisorMode && (
                                    <button className="rep-menu-item" onClick={handleOpenPreviewModal}>
                                        <Link size={15} />
                                        <span>Liberar prévia</span>
                                    </button>
                                )}

                                {isAdmin && !revisorMode && (
                                    <>
                                        <button
                                            className={`rep-menu-item ${isBlocked ? 'rep-menu-item-warning' : ''}`}
                                            onClick={() => { handleToggleBlocked(); setShowMenu(false); }}
                                        >
                                            {isBlocked ? <ShieldOff size={15} /> : <ShieldCheck size={15} />}
                                            <span>{isBlocked ? 'Apostila interditada' : 'Interditar apostila'}</span>
                                        </button>

                                        <button
                                            className="rep-menu-item rep-menu-item-drafts"
                                            onClick={() => { setShowDraftsModal(true); setShowMenu(false); }}
                                        >
                                            <Eye size={15} />
                                            <span>
                                                Ver sugestões de revisores
                                                {pendingDraftsCount > 0 && (
                                                    <span className="rep-drafts-badge">{pendingDraftsCount}</span>
                                                )}
                                            </span>
                                        </button>

                                        {resumo._id && (
                                            <>
                                                <div className="rep-menu-divider" />
                                                <button
                                                    className="rep-menu-item rep-menu-item-danger"
                                                    onClick={() => { handleDelete(); setShowMenu(false); }}
                                                >
                                                    <Trash2 size={15} />
                                                    <span>Excluir apostila</span>
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <EditorToolbar
                cards={cards}
                darkMode={darkMode}
                onSaveSelection={saveCurrentSelection}
                savedRangeRef={savedRangeRef}
                editorRef={editorRef}
                contentManager={contentManagerRef.current}
                quizManager={quizManager}
                tableManager={tableManagerRef.current}
                resizeManager={resizeManagerRef.current}
                pages={pages}
            />

            {/* Régua }
            <SimpleRuler
                editorRef={editorRef}
                currentIndent={rulerState.currentIndent}
                rulerWidth={rulerState.rulerWidth}
                onIndentChange={applyIndent}
                darkMode={darkMode}
            /> */}

            {/* Content */}
            <div className="rep-content">
                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onMouseUp={saveCurrentSelection}
                    onKeyUp={(e) => {
                        saveCurrentSelection();
                        // updateSelection();
                    }}
                    onFocus={() => {
                        // updateSelection();
                    }}
                    onClick={() => {
                        // updateSelection();
                    }}
                    onBlur={saveCurrentSelection}
                    onInput={(e) => {
                        // updateSelection();
                        const html = e.currentTarget.innerHTML;
                        if (html !== lastSavedHTMLRef.current) {
                            setHasUnsavedChanges(true);
                        }
                    }}
                    className="rep-free-editor"
                    style={{
                        direction: 'ltr',
                        textAlign: 'left'
                    }}
                />
            </div>

            {/* Page Navigator com Auto-Hide */}
            <AutoHideFooter
                threshold={300}
                darkMode={darkMode}
            >
                <PageNavigator
                    pages={pages}
                    currentPageIndex={currentPageIndex}
                    onPageChange={handlePageChange}
                    onAddPage={handleAddPage}
                    isEditing={true}
                    darkMode={darkMode}
                />
            </AutoHideFooter>

            {/* ✅ QUIZ: Modal Admin */}
            {quizModalOpen && editingQuizId && (
                <QuizAdminModal
                    quizData={editingQuizData}
                    onSave={handleSaveQuiz}
                    onClose={() => {
                        setQuizModalOpen(false);
                        setEditingQuizId(null);
                        setEditingQuizData(null);
                    }}
                    onDeleteQuiz={handleDeleteQuiz}
                    darkMode={darkMode}
                />
            )}

            {revisorMode && showReviewerPanel && (
                <ReviewerPanel
                    deckId={deckId!}
                    subdeckId={subdeckId!}
                    darkMode={darkMode}
                    currentPageId={pages[currentPageIndex]?.id ?? ''}
                    pageNotes={pageNotes}
                    reviewNote={reviewNote}
                    onReviewNoteChange={setReviewNote}
                    onPageNotesChange={setPageNotes}
                    onClose={() => setShowReviewerPanel(false)}
                />
            )}

            {showDraftsModal && (
                <ApostilaDraftsModal
                    deckId={deckId!}
                    subdeckId={subdeckId!}
                    darkMode={darkMode}
                    onClose={() => setShowDraftsModal(false)}
                    onLoadDraft={(context) => {
                        setPages(context.pages);
                        if (context.quizzes) {
                            setResumo(prev => prev ? { ...prev, quizzes: context.quizzes } : prev);
                        }
                        setCurrentPageIndex(0);
                        setEditorLoadTrigger(t => t + 1);
                        setHasUnsavedChanges(true);
                        setPendingDraftsCount(c => Math.max(0, c - 1));
                        setActiveDraft(context);
                    }}
                />
            )}

            {showImportModal && (
                <ImportApostilaModal
                    onImport={handleImport}
                    onClose={() => setShowImportModal(false)}
                    darkMode={darkMode}
                    multiple={false}
                />
            )}

            {showPageManager && (
                <PageManagerModal
                    pages={pages}
                    currentPageIndex={currentPageIndex}
                    onDeletePage={handleDeletePage}
                    onMovePage={handleMovePage}
                    onInsertPageAfter={handleInsertPageAfter}
                    onAddPageAtEnd={handleAddPage}
                    onNavigateToPage={(index) => { handlePageChange(index); }}
                    onRenamePage={handleRenamePage}
                    onClose={() => setShowPageManager(false)}
                    darkMode={darkMode}
                />
            )}

            {/* ── Modal Liberar Prévia ── */}
            {showPreviewModal && (
                <div className="rep-modal-overlay" onClick={handleClosePreviewModal}>
                    <div className="rep-modal rep-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="rep-modal-header">
                            <h3>Liberar Prévia</h3>
                            <button className="rep-modal-close" onClick={handleClosePreviewModal}>✕</button>
                        </div>

                        {!generatedPreviewUrl ? (
                            <>
                                <p className="rep-preview-desc">
                                    Gera um link temporário que qualquer pessoa pode acessar sem conta. Defina a duração e quantas páginas ficam disponíveis.
                                </p>

                                {/* Duração */}
                                <div className="rep-preview-duration-label">
                                    <Clock size={14} />
                                    <span>Duração do link</span>
                                </div>
                                <div className="rep-preview-durations">
                                    {(Object.keys(PREVIEW_DURATION_LABELS) as PreviewDuration[]).map((key) => (
                                        <button
                                            key={key}
                                            className={`rep-preview-duration-btn ${previewDuration === key ? 'active' : ''}`}
                                            onClick={() => setPreviewDuration(key)}
                                        >
                                            {PREVIEW_DURATION_LABELS[key]}
                                        </button>
                                    ))}
                                </div>

                                {/* Páginas liberadas */}
                                <div className="rep-preview-duration-label" style={{ marginTop: 16 }}>
                                    <Eye size={14} />
                                    <span>Páginas liberadas</span>
                                </div>
                                <div className="rep-preview-pages-row">
                                    <div className="rep-preview-pages-btns">
                                        <button
                                            className={`rep-preview-duration-btn ${previewPagesLimit === 0 ? 'active' : ''}`}
                                            onClick={() => setPreviewPagesLimit(0)}
                                        >
                                            Todas
                                        </button>
                                        {[1, 2, 3, 4, 5, 6, 8, 10].filter(n => n <= pages.length).map(n => (
                                            <button
                                                key={n}
                                                className={`rep-preview-duration-btn ${previewPagesLimit === n ? 'active' : ''}`}
                                                onClick={() => setPreviewPagesLimit(n)}
                                            >
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                    {previewPagesLimit > 0 && pages.length > 0 && (
                                        <p className="rep-preview-pages-hint">
                                            {previewPagesLimit} de {pages.length} {pages.length === 1 ? 'página' : 'páginas'} — as demais ficam bloqueadas
                                        </p>
                                    )}
                                </div>

                                <button
                                    className="rep-preview-generate-btn"
                                    onClick={handleGeneratePreview}
                                    disabled={isGeneratingPreview}
                                >
                                    {isGeneratingPreview ? (
                                        <>
                                            <Loader2 size={15} className="rep-spin" />
                                            Gerando...
                                        </>
                                    ) : (
                                        <>
                                            <Link size={15} />
                                            Gerar link
                                        </>
                                    )}
                                </button>

                                {/* Links ativos */}
                                {(isLoadingActiveTokens || activePreviewTokens.length > 0) && (
                                    <div className="rep-preview-active-section">
                                        <div className="rep-preview-active-title">Links ativos</div>
                                        {isLoadingActiveTokens ? (
                                            <div className="rep-preview-active-loading">Carregando...</div>
                                        ) : (
                                            <div className="rep-preview-active-list">
                                                {activePreviewTokens.map(t => {
                                                    const timeLeft = t.expiresAt === null
                                                        ? 'Sempre disponível'
                                                        : (() => {
                                                            const exp = new Date(t.expiresAt);
                                                            const diffMs = exp.getTime() - Date.now();
                                                            const diffMin = Math.floor(diffMs / 60000);
                                                            const diffH = Math.floor(diffMin / 60);
                                                            return diffH > 0
                                                                ? `${diffH}h ${diffMin % 60}min restantes`
                                                                : `${diffMin}min restantes`;
                                                        })();
                                                    return (
                                                        <div key={t._id} className="rep-preview-active-item">
                                                            <div className="rep-preview-active-info">
                                                                <span className="rep-preview-active-slug">/{t.urlSlug}</span>
                                                                <span className="rep-preview-active-time">
                                                                    <Clock size={11} />
                                                                    {timeLeft}
                                                                    {t.previewPagesLimit != null && ` · ${t.previewPagesLimit} pág.`}
                                                                </span>
                                                            </div>
                                                            <button
                                                                className="rep-preview-revoke-btn"
                                                                onClick={() => handleRevokePreviewToken(t.urlSlug)}
                                                                title="Revogar link"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <p className="rep-preview-success-msg">
                                    Link gerado! Válido por <strong>{PREVIEW_DURATION_LABELS[previewDuration]}</strong>
                                    {previewPagesLimit > 0 && ` · ${previewPagesLimit} ${previewPagesLimit === 1 ? 'página' : 'páginas'} liberadas`}
                                    {previewExpiresAt && previewDuration !== 'always' && (
                                        <span className="rep-preview-expires">
                                            {' '}— expira às {new Date(previewExpiresAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} de {new Date(previewExpiresAt).toLocaleDateString('pt-BR')}
                                        </span>
                                    )}
                                </p>
                                <div className="rep-preview-url-row">
                                    <input
                                        className="rep-preview-url-input"
                                        type="text"
                                        value={generatedPreviewUrl}
                                        readOnly
                                        onClick={(e) => (e.target as HTMLInputElement).select()}
                                    />
                                    <button
                                        className={`rep-preview-copy-btn ${previewCopied ? 'copied' : ''}`}
                                        onClick={handleCopyPreviewUrl}
                                        title="Copiar link"
                                    >
                                        {previewCopied ? <Check size={15} /> : <Copy size={15} />}
                                        {previewCopied ? 'Copiado!' : 'Copiar'}
                                    </button>
                                </div>
                                <button className="rep-preview-new-btn" onClick={() => { setGeneratedPreviewUrl(null); setPreviewExpiresAt(null); setPreviewCopied(false); }}>
                                    Gerar novo link
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* FAB flutuante de contexto do revisor (admin) */}
            {isAdmin && activeDraft && (
                <ReviewerFAB
                    activeDraft={activeDraft}
                    currentPageId={pages[currentPageIndex]?.id ?? ''}
                    darkMode={darkMode}
                />
            )}

            {/* Toast */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default ResumoEditorPage;